import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface LocationDist {
  name: string;
  address: string;
  lat: number;
  lng: number;
  type: string;
}

const officeLocations: LocationDist[] = [
  { name: "台北", address: "台北市萬華區漢中街195號", lat: 25.033, lng: 121.499, type: "北部服務據點" },
  { name: "新竹", address: "新竹縣竹北市自強南路372號3樓", lat: 24.827, lng: 121.013, type: "北部服務據點" },
  { name: "台中（設計總部）", address: "台中市北區中清路一段79號6樓", lat: 24.158, lng: 120.681, type: "設計總部" },
  { name: "台中（建築總部）", address: "台中市西屯區中工二路190號8樓", lat: 24.182, lng: 120.617, type: "建築總部" },
  { name: "台中（預製工法廠）", address: "台中市南區大慶街二段55-5號", lat: 24.120, lng: 120.663, type: "預製工法廠" },
  { name: "台南", address: "臺南市永康區中華路12號13樓之3", lat: 23.022, lng: 120.257, type: "南部服務據點" },
  { name: "屏東", address: "屏東縣九如鄉仁愛街45巷19號1樓", lat: 22.731, lng: 120.486, type: "南部服務據點" },
];

function haversine(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Rough lat/lng mapping for major Taiwan cities/districts
function guessCoords(input: string): { lat: number; lng: number } | null {
  const normalized = input.trim().replace(/台/g, "臺");
  const cityMap: Record<string, { lat: number; lng: number }> = {
    "臺北": { lat: 25.033, lng: 121.565 },
    "台北": { lat: 25.033, lng: 121.565 },
    "新北": { lat: 25.012, lng: 121.466 },
    "基隆": { lat: 25.128, lng: 121.739 },
    "桃園": { lat: 24.993, lng: 121.301 },
    "新竹": { lat: 24.827, lng: 121.013 },
    "苗栗": { lat: 24.571, lng: 120.822 },
    "臺中": { lat: 24.147, lng: 120.674 },
    "台中": { lat: 24.147, lng: 120.674 },
    "彰化": { lat: 24.076, lng: 120.544 },
    "南投": { lat: 23.960, lng: 120.972 },
    "雲林": { lat: 23.699, lng: 120.527 },
    "嘉義": { lat: 23.481, lng: 120.453 },
    "臺南": { lat: 22.998, lng: 120.213 },
    "台南": { lat: 22.998, lng: 120.213 },
    "高雄": { lat: 22.627, lng: 120.301 },
    "屏東": { lat: 22.551, lng: 120.549 },
    "宜蘭": { lat: 24.751, lng: 121.758 },
    "花蓮": { lat: 23.991, lng: 121.611 },
    "臺東": { lat: 22.757, lng: 121.145 },
    "台東": { lat: 22.757, lng: 121.145 },
  };
  for (const [city, coords] of Object.entries(cityMap)) {
    if (normalized.includes(city)) return coords;
  }
  return null;
}

function calcDriveTime(distKm: number) {
  const timeMin = distKm * 1.5;
  if (timeMin < 60) return `${Math.round(timeMin)} 分鐘`;
  const h = Math.floor(timeMin / 60);
  const m = Math.round(timeMin % 60);
  return m === 0 ? `${h} 小時` : `${h} 小時 ${m} 分鐘`;
}

export default function DistanceCalculatorSection() {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [result, setResult] = useState<{
    nearest: LocationDist;
    distance: number;
    driveTime: string;
    all: { loc: LocationDist; distance: number }[];
  } | null>(null);
  const [error, setError] = useState("");

  const handleCalc = useCallback(() => {
    setError("");
    setResult(null);
    if (!input.trim()) {
      setError("請輸入您的所在縣市或地址");
      return;
    }
    const coords = guessCoords(input);
    if (!coords) {
      setError("無法辨識地址位置，請嘗試輸入縣市名稱（如「台中」、「台北」等）");
      return;
    }

    const withDist = officeLocations.map((loc) => ({
      loc,
      distance: haversine(coords.lat, coords.lng, loc.lat, loc.lng),
    }));

    withDist.sort((a, b) => a.distance - b.distance);

    setResult({
      nearest: withDist[0].loc,
      distance: withDist[0].distance,
      driveTime: calcDriveTime(withDist[0].distance),
      all: withDist,
    });
  }, [input]);

  return (
    <section className="section-padding bg-cream">
      <div className="container-main px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="tag-label mb-4 inline-block">
              {t("locations.calc.label")}
            </span>
            <h2 className="text-graphite">{t("locations.calc.title")}</h2>
            <p className="text-medium-gray mt-3 max-w-xl mx-auto">
              {t("locations.calc.subtitle")}
            </p>
          </div>

          {/* Input card */}
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-warm-gray/50">
            <div className="flex flex-col sm:flex-row gap-3 mb-2">
              <div className="relative flex-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-medium-gray">
                  <i className="ri-map-pin-line" />
                </span>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCalc();
                  }}
                  placeholder="輸入您的所在縣市或地址..."
                  className="w-full rounded-xl border border-warm-gray/60 bg-cream pl-11 pr-4 py-3 text-graphite text-base focus:outline-none focus:ring-2 focus:ring-champagne/50"
                />
              </div>
              <button
                onClick={handleCalc}
                className="btn-primary whitespace-nowrap px-6 py-3 text-base gap-2"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-route-line" />
                </span>
                計算距離
              </button>
            </div>

            {error && (
              <p className="text-red-600 text-sm mt-3 flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line" />
                </span>
                {error}
              </p>
            )}

            {/* Result */}
            {result && (
              <div className="mt-6 border-t border-warm-gray/40 pt-6">
                {/* Nearest highlight */}
                <div className="bg-champagne/10 rounded-2xl p-5 md:p-6 mb-6 border border-champagne/20">
                  <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                    <div className="flex-1">
                      <p className="text-medium-gray text-sm mb-1">距離您最近的據點</p>
                      <h3 className="text-graphite text-xl font-semibold">
                        {result.nearest.name}
                        <span className="text-medium-gray font-normal text-base ml-2">
                          {result.nearest.type}
                        </span>
                      </h3>
                      <p className="text-medium-gray text-base mt-1">
                        {result.nearest.address}
                      </p>
                    </div>

                    <div className="flex flex-row md:flex-col gap-4 md:gap-2 md:text-right shrink-0">
                      <div>
                        <p className="text-medium-gray text-sm">直線距離</p>
                        <p className="text-graphite font-bold text-lg">
                          {result.distance.toFixed(1)} km
                        </p>
                      </div>
                      <div>
                        <p className="text-medium-gray text-sm">預估車程</p>
                        <p className="text-champagne font-bold text-lg">
                          {result.driveTime}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* All locations sorted */}
                <div>
                  <h4 className="text-graphite font-semibold mb-3 text-base">
                    其他據點距離排序
                  </h4>
                  <div className="flex flex-col gap-2">
                    {result.all.map((item, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm ${
                          i === 0
                            ? "bg-champagne/10 border border-champagne/20"
                            : "bg-cream border border-warm-gray/40"
                        }`}
                      >
                        <span
                          className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${
                            i === 0
                              ? "bg-champagne text-white"
                              : "bg-warm-gray text-graphite"
                          }`}
                        >
                          {i + 1}
                        </span>
                        <span className="flex-1 text-graphite font-medium truncate">
                          {item.loc.name}
                          <span className="text-medium-gray font-normal ml-1.5 hidden sm:inline">
                            {item.loc.type}
                          </span>
                        </span>
                        <span className="text-medium-gray shrink-0">
                          {item.distance.toFixed(1)} km
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Note */}
            <p className="text-medium-gray text-sm mt-4 leading-relaxed">
              距離為直線距離估算，實際車程會因路線與交通狀況有所不同。建議出發前使用 Google Maps 規劃路線。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}